import React, { useState, useEffect, useRef, createRef } from "react";

import Select from "react-select";
import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import FilterTags from "../Tags/FilterTags";
import { shortDateYMD, getDateRange } from "../../utils/DateHelper";
import { isEmpty } from "lodash";
import {
    COMPARE_FILTER_TYPE,
    DATES_CONSTANTS,
    FIELD_TYPES,
} from "../../utils/constants";
import { Trans, useTranslation } from "react-i18next";
import { removeItem, setObject } from "../../utils/StorageHelpers";
import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import FilterOptionWrapper from "../FilterChildren/FilterOptionWrapper";
import { stringifyIfNumber } from "../../utils/Helpers";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";

const FilterDropdown = ({
    filterOptions,
    filters,
    setFilters,
    applyFilters,
    disabled,
    component,
    initialFilter = null,
    setInitialFilter,
    appliedFilters,
    setAppliedFilters,
    saveToStorage = true,
}) => {
    const [filterDropdown, setFilterDropdown] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [selectedFiltersCount, setSelectedFiltersCount] = useState(0);
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        setRoles(getRoles());
    }, []);

    const isFilterDropdownOpen = useRef(false);
    const appliedFiltersRef = useRef({});
    const fieldsRef = useRef(Array(filterOptions.length).fill(createRef()));

    const { t } = useTranslation();

    const compareOptions = [
        {
            label: t(COMPARE_FILTER_TYPE.lessThan),
            value: COMPARE_FILTER_TYPE.lessThan,
        },
        {
            label: t(COMPARE_FILTER_TYPE.equals),
            value: COMPARE_FILTER_TYPE.equals,
        },
        {
            label: t(COMPARE_FILTER_TYPE.greaterThan),
            value: COMPARE_FILTER_TYPE.greaterThan,
        },
    ];

    const toggleFilterButton = () => {
        setFilterDropdown(!filterDropdown);
        isFilterDropdownOpen.current = !isFilterDropdownOpen.current;
    };

    useEffect(() => {
        let initiallySelected = {};
        filterOptions.forEach((option) => {
            initiallySelected[option.name] = false;
        });
        setSelectedFilters(initiallySelected);
    }, []);

    useEffect(() => {
        if (initialFilter && filters) {
            setSelectedFiltersCount(initialFilter.length);
            let newSelected = { ...selectedFilters };
            initialFilter.forEach((option) => {
                newSelected[option.name] = true;
            });

            let filter = {};
            filterOptions.forEach((option) => {
                if (newSelected[option.name]) {
                    filter[option.name] = filters[option.name];
                }
            });
            setAppliedFilters(filter);
            setSelectedFilters(newSelected);
            setInitialFilter(null);
        }
    }, [initialFilter, filters]);

    useEffect(() => {
        appliedFiltersRef.current = appliedFilters;
        if (isEmpty(appliedFilters)) {
            setSelectedFiltersCount(0);
            setSelectedFilters({});
        } else {
            let newSelected = {};
            let count = 0;
            Object.keys(appliedFilters).forEach((key) => {
                if (!isEmpty(stringifyIfNumber(appliedFilters[key]))) {
                    newSelected[key] = true;
                    count++;
                }
            });
            setSelectedFilters(newSelected);
            setSelectedFiltersCount(count);
            saveToStorage &&
                setObject(`${component}-filters`, appliedFilters, true);
        }
    }, [appliedFilters]);

    const closeFilterButton = () => {
        setFilterDropdown(false);
        isFilterDropdownOpen.current = false;
    };

    const incrementFilter = () => {
        setSelectedFiltersCount(selectedFiltersCount + 1);
    };

    const decrementFilter = () => {
        setSelectedFiltersCount(selectedFiltersCount - 1);
    };

    const focusFieldRef = (index) => {
        setTimeout(() => {
            index !== null && fieldsRef.current[index].focus();
        }, 1);
    };

    const handleCheckboxClicked = (
        name,
        child = "",
        date = false,
        index = null
    ) => {
        let newFilters = { ...filters };
        if (selectedFilters[name] && date && child) {
            const otherChild =
                child === DATES_CONSTANTS.startDate
                    ? DATES_CONSTANTS.endDate
                    : DATES_CONSTANTS.startDate;
            if (filters[name][otherChild]) {
                newFilters[name][child] = "";
                setFilters(newFilters);
                return;
            } else {
                newFilters[name] = {};
                setFilters(newFilters);
                decrementFilter();
            }
        } else if (selectedFilters[name] && date) {
            newFilters[name] = {};
            setFilters(newFilters);
            decrementFilter();
        } else if (selectedFilters[name] && child) {
            if (
                Object.values(filters[name]).filter((obj) => !!obj).length === 1
            ) {
                newFilters[name] = {};
                decrementFilter();
            } else {
                newFilters[name][child] = "";
            }
            setFilters(newFilters);
        } else if (selectedFilters[name]) {
            decrementFilter();
            setFilters({ ...newFilters, [name]: "" });
        } else {
            incrementFilter();
        }

        let newSelectedFilters = { ...selectedFilters };
        newSelectedFilters[name] = !newSelectedFilters[name];
        focusFieldRef(index);
        setSelectedFilters(newSelectedFilters);
    };

    const handleClear = () => {
        setSelectedFilters({});
        setSelectedFiltersCount(0);

        setFilters({});
        setAppliedFilters({});
        closeFilterButton();
        saveToStorage && removeItem(`${component}-filters`, true);
        applyFilters({});
    };

    const handleInputChangeExplicit = (propName, value) => {
        const newFilters = {
            ...filters,
            [propName]: value,
        };
        setFilters(newFilters);
        return newFilters;
    };

    const handleInputChange = (event) => {
        setFilters({
            ...filters,
            [event.target.name]: event.target.value,
        });
    };

    const handleDoubleInputsChange = (event, mainKey, subKey) => {
        const initial = filters[mainKey] ? filters[mainKey] : {};
        setFilters({
            ...filters,
            [mainKey]: {
                ...initial,
                [subKey]: event.target.value,
            },
        });
    };

    const handleCompareChange = (name, compare) => {
        setFilters({
            ...filters,
            [name]: compare,
        });
    };

    const handleMinMaxValidation = (min, max, filterName) => {
        if (min && Number(min) > filters[filterName]) {
            return handleInputChangeExplicit(filterName, Number(min));
        } else if (max && Number(max) < filters[filterName]) {
            return handleInputChangeExplicit(filterName, Number(max));
        }
    };

    const handleDateChange = (name, event) => {
        let newDate = new Date(event.target.value);
        let newFilters = { ...filters };
        if (!newFilters[event.target.name]) {
            newFilters[event.target.name] = {};
        }

        const timezoneHours = newDate.getTimezoneOffset() / -60;

        if (DATES_CONSTANTS.startDate === name) {
            newDate.setHours(newDate.getHours() - timezoneHours);
            newDate.setMinutes(0);
        } else if (DATES_CONSTANTS.endDate === name) {
            newDate.setHours(newDate.getHours() - timezoneHours);
            newDate.setHours(23);
            newDate.setMinutes(59);
        }

        newFilters[event.target.name] = { ...newFilters[event.target.name] };
        newFilters[event.target.name][name] = newDate;
        setFilters({ ...newFilters });
    };

    const handleSelectChange = (name, { value }) => {
        setFilters({
            ...filters,
            [name]: value,
        });
    };
    const handleMultiSelectChange = (name, value) => {
        setFilters({
            ...filters,
            [name]: value,
        });
    };

    const trimStringFilters = (filterData, propName) => {
        return typeof filterData[propName] === "string"
            ? filterData[propName].trim()
            : filterData[propName];
    };

    const handleApplyFilter = (
        deletedProp = null,
        deletedChildProp = null,
        newFilters = null
    ) => {
        closeFilterButton();
        let filter = {};
        filterOptions.forEach((option) => {
            if (selectedFilters[option.name]) {
                filter[option.name] = trimStringFilters(
                    newFilters || filters,
                    option.name
                );
            }
        });

        if (Object.keys(filter).length > 0) {
            if (
                deletedProp &&
                (!deletedChildProp ||
                    Object.keys(filter[deletedProp]).length === 0)
            )
                delete filter[deletedProp];

            if (deletedProp && deletedChildProp)
                delete filter[deletedProp][deletedChildProp];
        }

        setFilters(filter);
        setAppliedFilters(filter);
        saveToStorage && setObject(`${component}-filters`, filter, true);
        applyFilters(filter);
    };

    const handleCloseTag = (name, child = "", date = false) => {
        handleCheckboxClicked(name, child, date);
        handleApplyFilter(name, child);
    };

    const validateDropdownSelection = (existingFilters) => {
        if (existingFilters) {
            setFilters(existingFilters);
            let newSelected = { ...selectedFilters };
            Object.keys(existingFilters).forEach((filter) => {
                if (!isEmpty(stringifyIfNumber(existingFilters[filter]))) {
                    newSelected[filter] = true;
                }
            });
            setSelectedFilters(newSelected);
            setSelectedFiltersCount(Object.keys(newSelected).length);
        }
    };

    const filterRef = useClickOutside(() => {
        if (isFilterDropdownOpen.current) {
            validateDropdownSelection(appliedFiltersRef.current);
            closeFilterButton();
        }
    });

    const renderDateOption = (filter, index) => {
        const date = filters[filter.name];
        const limit =
            filter.monthBoundary &&
            getDateRange(date?.startDate, date?.endDate, filter.monthBoundary);
        return (
            <FilterOptionWrapper
                filter={filter}
                index={index}
                component={component}
                selectedFilters={selectedFilters}
                handleCheckboxClicked={handleCheckboxClicked}
                isDate={true}
                key={index}
            >
                <div className="flex items-center mb-2">
                    <span className="mr-2">
                        <Trans i18nKey="from">From</Trans>{" "}
                    </span>
                    <input
                        type="date"
                        name={filter.name}
                        value={
                            date?.startDate ? shortDateYMD(date.startDate) : ""
                        }
                        onChange={(event) => {
                            handleDateChange(DATES_CONSTANTS.startDate, event);
                        }}
                        min={limit?.min || filter?.min || ""}
                        max={
                            date?.endDate
                                ? shortDateYMD(date.endDate)
                                : filter?.max || ""
                        }
                        onKeyDown={(e) => {
                            e.preventDefault();
                        }}
                        className="input-element"
                        id="date-filter"
                    />
                </div>

                <div className="flex items-center">
                    <span className="mr-6">
                        <Trans i18nKey="to">To</Trans>
                    </span>
                    <input
                        type="date"
                        name={filter.name}
                        value={date?.endDate ? shortDateYMD(date.endDate) : ""}
                        onChange={(event) => {
                            handleDateChange(DATES_CONSTANTS.endDate, event);
                        }}
                        min={
                            date?.startDate
                                ? shortDateYMD(date.startDate)
                                : filter?.min || ""
                        }
                        max={limit?.max || filter?.max || ""}
                        onKeyDown={(e) => {
                            e.preventDefault();
                        }}
                        className="input-element"
                    />
                </div>
            </FilterOptionWrapper>
        );
    };

    const renderInputOption = (filter, index) => {
        const min = filter?.min?.toString() || "";
        const max = filter?.max?.toString() || "";
        return (
            <FilterOptionWrapper
                filter={filter}
                index={index}
                component={component}
                handleCheckboxClicked={handleCheckboxClicked}
                selectedFilters={selectedFilters}
                key={index}
            >
                <div className="flex items-center justify-between">
                    <input
                        type={filter.inputType || "text"}
                        className="input-element"
                        placeholder={filter.placeholder}
                        name={filter.name}
                        onChange={handleInputChange}
                        min={min}
                        max={max}
                        onBlur={() =>
                            handleMinMaxValidation(min, max, filter.name)
                        }
                        onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                                const newFilters = handleMinMaxValidation(
                                    min,
                                    max,
                                    filter.name
                                );
                                handleApplyFilter(null, null, newFilters);
                            }
                        }}
                        id={`${filter.name}-text-filter`}
                        value={filters[filter.name] || ""}
                        ref={(ref) => (fieldsRef.current[index] = ref)}
                    />
                </div>
            </FilterOptionWrapper>
        );
    };

    const renderDropdownOption = (filter, index) => {
        return (
            <FilterOptionWrapper
                filter={filter}
                index={index}
                component={component}
                handleCheckboxClicked={handleCheckboxClicked}
                selectedFilters={selectedFilters}
                key={index}
            >
                <div className="filter-select-wrapper">
                    <Select
                        id={`${filter.name}-select-filter`}
                        name={filter.name}
                        placeholder={
                            filter.placeholder ||
                            t("selectPlaceholderWithoutEntity")
                        }
                        options={filter.options}
                        onChange={(event) =>
                            filter.isMulti
                                ? handleMultiSelectChange(filter.name, event)
                                : handleSelectChange(filter.name, event)
                        }
                        value={
                            filter.isMulti
                                ? filters[filter.name]
                                : filter.options.find(
                                      (opt) =>
                                          opt.value === filters[filter.name]
                                  ) || ""
                        }
                        theme={getReactSelectTheme}
                        styles={getReactSelectSmallStyling(
                            false,
                            filter.isMulti ? "auto" : 30
                        )}
                        getOptionValue={(option) =>
                            option.searchable || option.label
                        }
                        isMulti={filter.isMulti}
                    />
                </div>
            </FilterOptionWrapper>
        );
    };

    const renderDoubleInputOption = (filter, index) => {
        return (
            <FilterOptionWrapper
                filter={filter}
                index={index}
                component={component}
                handleCheckboxClicked={handleCheckboxClicked}
                selectedFilters={selectedFilters}
                key={index}
            >
                {filter.fields?.map((field, index) => {
                    return (
                        <div
                            className="grid grid-cols-5 items-center mb-2 "
                            key={index}
                        >
                            <span>{field.label}</span>
                            <input
                                type={field.type}
                                name={field.name}
                                value={filters[filter.name]?.[field.name] || ""}
                                placeholder={field.placeholder}
                                min={field?.min?.toString() || ""}
                                max={field?.max?.toString() || ""}
                                onChange={(event) => {
                                    handleDoubleInputsChange(
                                        event,
                                        filter.name,
                                        field.name
                                    );
                                }}
                                className="input-element col-span-4"
                            />
                        </div>
                    );
                })}
            </FilterOptionWrapper>
        );
    };

    const renderCompareOption = (filter, index) => {
        return (
            <FilterOptionWrapper
                filter={filter}
                index={index}
                component={component}
                handleCheckboxClicked={handleCheckboxClicked}
                selectedFilters={selectedFilters}
                key={index}
            >
                <div className="filter-select-wrapper mb-3">
                    <Select
                        name="target"
                        options={filter.options}
                        placeholder={filter.targetPlaceholder}
                        onChange={(event) =>
                            handleCompareChange(filter.name, {
                                ...filters[filter.name],
                                target: event.value,
                            })
                        }
                        value={
                            filter.options.find(
                                (opt) =>
                                    opt.value === filters[filter.name]?.target
                            ) || ""
                        }
                        theme={getReactSelectTheme}
                        styles={getReactSelectSmallStyling(true, 33.2)}
                    />
                </div>
                <div className="flex items-center justify-center gap-2">
                    <div className="filter-select-wrapper flex-1">
                        <Select
                            name="compare"
                            options={compareOptions}
                            placeholder={t("comparePlaceholder")}
                            onChange={(event) =>
                                handleCompareChange(filter.name, {
                                    ...filters[filter.name],
                                    compare: event.value,
                                })
                            }
                            value={
                                compareOptions.find(
                                    (opt) =>
                                        opt.value ===
                                        filters[filter.name]?.compare
                                ) || ""
                            }
                            theme={getReactSelectTheme}
                            styles={getReactSelectSmallStyling(true, 33.2)}
                        />
                    </div>
                    <div className="flex-1">
                        <input
                            type="number"
                            className="input-element"
                            placeholder={filter.placeholder}
                            name={filter.name}
                            onChange={(event) =>
                                handleCompareChange(event.target.name, {
                                    ...filters[filter.name],
                                    value: event.target.value,
                                })
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleApplyFilter();
                                }
                            }}
                            value={filters[filter.name]?.value || ""}
                            ref={(ref) => (fieldsRef.current[index] = ref)}
                        />
                    </div>
                </div>
            </FilterOptionWrapper>
        );
    };

    const renderFilterOptions = () => {
        return (
            filterOptions &&
            filters &&
            filterOptions.map((filter, index) => {
                if (filter.disabled) return;

                if (!filter.roles || isAuthorized(filter.roles, roles)) {
                    switch (filter.type) {
                        case FIELD_TYPES.Date:
                            return renderDateOption(filter, index);
                        case FIELD_TYPES.Dropdown:
                            return renderDropdownOption(filter, index);
                        case FIELD_TYPES.Input:
                            return renderInputOption(filter, index);
                        case FIELD_TYPES.Compare:
                            return renderCompareOption(filter, index);
                        case FIELD_TYPES.DoubleInput:
                            return renderDoubleInputOption(filter, index);
                    }
                }
                return;
            })
        );
    };

    return (
        <>
            <FilterTags
                filterOptions={filterOptions}
                appliedFilters={appliedFilters}
                handleCloseTag={handleCloseTag}
                selectedFilters={selectedFilters}
            />
            <div className="filter-content-wrapper" ref={filterRef}>
                <button
                    className={`default-orange-outline-btn flex items-center ${
                        disabled && "disabled"
                    }`}
                    data-element="filter-items-list"
                    onClick={toggleFilterButton}
                    disabled={disabled}
                    id="filter-dropdown-btn"
                >
                    <div className="flex items-center">
                        <div className="flex items-center">
                            <i className="fas fa-filter"></i>
                            <span className="ml-2">
                                <Trans i18nKey="filter">Filter</Trans>
                            </span>
                        </div>
                        <div
                            className={`number-checked-wrapper ${
                                selectedFiltersCount === 0 && "hidden"
                            }`}
                        >
                            {selectedFiltersCount}
                        </div>
                    </div>
                </button>
                <div
                    className={`filter-items shadow ${
                        filterDropdown ? "show" : ""
                    }`}
                    id={`${component}-filter-items-list`}
                >
                    <div className="filter-items-header border-bottom flex items-center justify-between">
                        <button
                            onClick={handleClear}
                            className="default-orange-btn clear-filter-btn"
                            id="filter-clear-btn"
                        >
                            <Trans i18nKey="clear">Clear</Trans>
                        </button>
                        <span className="filter-title">
                            <Trans i18nKey="filter">Filter</Trans>
                        </span>
                        <button
                            onClick={handleApplyFilter}
                            className="default-orange-btn"
                            id="filter-done-btn"
                        >
                            <Trans i18nKey="done">Done</Trans>
                        </button>
                    </div>
                    {renderFilterOptions()}
                </div>
            </div>
        </>
    );
};

export default FilterDropdown;
